import React from 'react';
import {
  MINIMAL_ERYTHEMAL_DOSE,
  OTHER_FACTOR_MED_REDUCTIONS,
  CLOUD_COVER_FACTORS,
  SURFACE_FACTORS,
  SKIN_TYPE_DATA,
} from './utils';
import './BreakdownModal.css';

const FACTOR_LABELS = {
  pregnant: 'Pregnant',
  breastfeeding: 'Breastfeeding',
  skinCancer: 'History of skin cancer',
  rosacea: 'Rosacea',
  eczema: 'Eczema',
  photosensitizing: 'Photosensitizing conditions',
  acne: 'Acne medication',
  antibiotics: 'Antibiotics',
  retinoids: 'Retinoids',
  chemotherapy: 'Chemotherapy drugs',
  immunosuppressants: 'Immunosuppressants',
  diuretics: 'Diuretics',
  tattoos: 'Recent tattoos',
  recentProcedures: 'Recent skin procedures',
};

const CLOUD_LABELS = {
  clear: 'Clear sky',
  scattered: 'Scattered clouds',
  broken: 'Broken clouds',
  overcast: 'Overcast',
};

const SURFACE_LABELS = {
  none: 'None',
  snow: 'Snow',
  sand: 'Sand',
  water: 'Water',
  grass: 'Grass',
  concrete: 'Concrete',
  asphalt: 'Asphalt',
  soil: 'Soil',
};

const BreakdownModal = ({
  isOpen,
  onClose,
  isDark,
  skinType,
  uvIndex,
  cloudCover,
  surfaceType,
  elevation,
  otherFactors,
  safeExposureTime,
}) => {
  if (!isOpen) return null;

  const baseMED = MINIMAL_ERYTHEMAL_DOSE[skinType];
  const skinLabel = SKIN_TYPE_DATA[skinType]?.label || `Type ${skinType}`;

  // Compute active risk factors
  const activeFactors = otherFactors
    ? Object.entries(otherFactors).filter(
        ([key, active]) => active && OTHER_FACTOR_MED_REDUCTIONS[key]
      )
    : [];

  const totalReduction = activeFactors.reduce(
    (sum, [key]) => sum + OTHER_FACTOR_MED_REDUCTIONS[key],
    0
  );
  const adjustedFactor = Math.max(0.1, 1 - totalReduction);
  const adjustedMED = baseMED * adjustedFactor;

  // Exposure factors
  const cloudFactor = CLOUD_COVER_FACTORS[cloudCover] || 1;
  const surfaceFactor = SURFACE_FACTORS[surfaceType] || 1;
  const altitudeKm = (elevation || 0) * 0.0003048;
  const altitudeFactor = 1 + altitudeKm * 0.06;

  // Build summary
  let summaryText;
  if (uvIndex === 0) {
    summaryText = "UV index is 0 — no burn risk right now.";
  } else if (safeExposureTime === null) {
    summaryText = "UV index is 0 — no burn risk right now.";
  } else if (safeExposureTime > 480) {
    summaryText = "According to the upcoming UV, you won't burn in the next 8 hours.";
  } else {
    const hours = Math.floor(safeExposureTime / 60);
    const minutes = safeExposureTime % 60;
    const timeStr =
      hours > 0 && minutes > 0
        ? `${hours} hr ${minutes} min`
        : hours > 0
        ? `${hours} hr`
        : `${minutes} min`;
    summaryText = `According to the upcoming UV, you'll burn without sunscreen in ${timeStr}.`;
  }

  return (
    <div className="breakdown-overlay" onClick={onClose}>
      <div
        className="breakdown-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: isDark ? '#2a2a2a' : '#ffffff',
          color: isDark ? '#ffffff' : '#000000',
        }}
      >
        <div className="breakdown-header">
          <h2>Breakdown</h2>
          <button className="breakdown-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="breakdown-columns">
          {/* Left column: Tolerance */}
          <div className="breakdown-column">
            <h3 className="column-title">Your tolerance</h3>
            <div className="breakdown-row">
              <span className="breakdown-label">{skinLabel}</span>
              <span className="breakdown-value">{baseMED} MED</span>
            </div>
            {activeFactors.map(([key]) => (
              <div className="breakdown-row risk-factor" key={key}>
                <span className="breakdown-label">{FACTOR_LABELS[key]}</span>
                <span className="breakdown-value negative">
                  -{Math.round(OTHER_FACTOR_MED_REDUCTIONS[key] * 100)}%
                </span>
              </div>
            ))}
            {activeFactors.length > 0 && (
              <div className="breakdown-row adjusted">
                <span className="breakdown-label">Adjusted tolerance</span>
                <span className="breakdown-value">
                  {adjustedMED.toFixed(2)} MED
                </span>
              </div>
            )}
          </div>

          {/* Right column: Exposure */}
          <div className="breakdown-column">
            <h3 className="column-title">Exposure factors</h3>
            <div className="breakdown-row">
              <span className="breakdown-label">UV Index</span>
              <span className="breakdown-value">{uvIndex}</span>
            </div>
            <div className="breakdown-row">
              <span className="breakdown-label">
                {CLOUD_LABELS[cloudCover] || 'Clear sky'}
              </span>
              <span className="breakdown-value">
                {cloudFactor < 1
                  ? `−${Math.round((1 - cloudFactor) * 100)}% UV`
                  : 'No change'}
              </span>
            </div>
            {surfaceType !== 'none' && (
              <div className="breakdown-row">
                <span className="breakdown-label">
                  {SURFACE_LABELS[surfaceType]}
                </span>
                <span className="breakdown-value positive">
                  +{Math.round((surfaceFactor - 1) * 100)}% UV
                </span>
              </div>
            )}
            {elevation > 0 && (
              <div className="breakdown-row">
                <span className="breakdown-label">
                  {elevation.toLocaleString()} ft elevation
                </span>
                <span className="breakdown-value positive">
                  +{((altitudeFactor - 1) * 100).toFixed(1)}% UV
                </span>
              </div>
            )}
          </div>
        </div>

        <div
          className="breakdown-summary"
          style={{
            backgroundColor: isDark
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(0, 0, 0, 0.04)',
          }}
        >
          {summaryText}
        </div>

        <div className="breakdown-footer">
          <button className="breakdown-close-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BreakdownModal;
